import React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { MessageCircle, Clock, User } from "lucide-react";
import { getUserConversations } from "~/lib/functions/messaging";
import { formatDistanceToNow } from "date-fns";

export const Route = createFileRoute("/_user/messages/")({
  head: () => ({
    title: "My Coaching Conversations | Dream Coach Match",
    meta: [
      {
        name: "description",
        content:
          "Access all your coaching conversations and stay connected with your coaches for ongoing support and guidance.",
      },
      {
        name: "robots",
        content: "noindex, nofollow",
      },
    ],
  }),
  component: MessagesPage,
});

function MessagesPage() {
  const {
    data: conversationsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["user-conversations"],
    queryFn: () => getUserConversations({ data: { limit: 50, offset: 0 } }),
  });

  const conversations = conversationsData?.conversations || [];

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Messages</h1>
          <p className="text-gray-600">Your conversations with coaches</p>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent>
                <div className="flex items-start space-x-4">
                  <div className="h-12 w-12 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/4 rounded bg-gray-200" />
                    <div className="h-3 w-3/4 rounded bg-gray-200" />
                    <div className="h-3 w-1/2 rounded bg-gray-200" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Messages</h1>
        </div>
        <Card className="border-red-200 bg-red-50">
          <CardContent>
            <p className="text-red-600">
              Failed to load conversations. Please try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Messages</h1>
        <p className="text-gray-600">Your conversations with coaches</p>
      </div>

      {conversations.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <MessageCircle className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h2 className="mb-2 text-xl font-semibold">No messages yet</h2>
            <p className="mb-6 text-gray-600">
              Start a conversation with a coach by visiting their profile and
              clicking "Contact Me"
            </p>
            <Link to="/coach-discovery">
              <Button>Find Coaches</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {conversations.map((conversation) => {
            const coachName =
              `${conversation.otherParticipant.firstName || ""} ${
                conversation.otherParticipant.lastName || ""
              }`.trim() || "Coach";

            return (
              <Link
                key={conversation.id}
                to="/messages/$conversationId"
                params={{ conversationId: conversation.id }}
              >
                <Card className="transition-colors hover:bg-gray-50">
                  <CardContent>
                    <div className="flex items-start space-x-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage
                          src={
                            conversation.otherParticipant.profilePictureUrl ||
                            undefined
                          }
                          alt={coachName}
                        />
                        <AvatarFallback>
                          <User className="h-6 w-6" />
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900">
                              {coachName}
                            </h3>
                            {conversation.otherParticipant.username && (
                              <span className="text-sm text-gray-500">
                                @{conversation.otherParticipant.username}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {conversation.unreadCount > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {conversation.unreadCount}
                              </Badge>
                            )}
                            <div className="flex items-center text-sm text-gray-500">
                              <Clock className="mr-1 h-3 w-3" />
                              {conversation.lastMessageAt
                                ? formatDistanceToNow(
                                    new Date(conversation.lastMessageAt),
                                    {
                                      addSuffix: true,
                                    },
                                  )
                                : "Unknown"}
                            </div>
                          </div>
                        </div>

                        <h4 className="mt-1 font-medium text-gray-800 truncate">
                          {conversation.subject}
                        </h4>

                        <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                          {conversation.senderType === "user"
                            ? "You: "
                            : `${coachName}: `}
                          {conversation.lastMessage.content}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {conversations.length > 0 && (
        <div className="mt-8 rounded-lg bg-blue-50 p-4">
          <h3 className="font-medium text-blue-900">
            💡 Tips for messaging coaches
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-blue-800">
            <li>• Be specific about your goals and what you're looking for</li>
            <li>
              • Ask questions about their coaching approach and experience
            </li>
            <li>• Share relevant background about your situation</li>
            <li>• Be respectful of their time and expertise</li>
          </ul>
        </div>
      )}
    </div>
  );
}
